import { useMemo, useState } from 'react'

import type { QuantMarketState } from '@/app/hooks/useQuantMarket'
import { SPAN_BARS, type QuantManualSpan } from '@/app/lib/quant/manualSpan'
import type { ManualIndicator } from '@/app/lib/quant/quantApi'
import { buildMarketCells, buildMarketModel, marketEndpoint } from '@/app/lib/quant/marketModel'
import { QuantChart, type ChartViewCommand } from '@/ui/QuantChart'
import { QuantReadoutCells } from '@/ui/QuantReadout'
import { QuantViewNotice } from '@/ui/QuantViewNotice'
import { viewFailureEffect } from '@/app/lib/quant/quantErrors'
import type { ViewFailure } from '@/app/lib/quant/viewProtocol'

export interface QuantManualChartProps {
  symbol: string
  tf: string
  span: QuantManualSpan
  active: ManualIndicator[]
  /** The market stream state (`useQuantMarket`, owned by the page so the toolbar can read it too). */
  state: QuantMarketState
  /** The user cancelled the open (the hook's `cancelled`). */
  cancelled?: boolean
  onReconnect?: () => void
  onCancel?: () => void
}

const widthOf = (span: QuantManualSpan): number | 'all' => (span === 'all' ? 'all' : SPAN_BARS[span])

/**
 * The live chart and readout strip of Manual trade (spec 036, handoff 4.3;
 * on Lightweight Charts since spec 038 P22). The chart owns the visible range;
 * this component only sends it a `reset` when the pair, timeframe or window
 * changes (the selected width at the newest bar). Candles tick every second and
 * a new bar opens on each timeframe boundary: the view follows the newest bar
 * only while it is at it. The readout follows the crosshair (R9): O H L C and
 * the line indicators show the hovered bar, everything else stays latest.
 */
export function QuantManualChart({ symbol, tf, span, active, state, cancelled = false, onReconnect = () => {}, onCancel = () => {} }: QuantManualChartProps) {
  const [view, setView] = useState<{ seq: number; command: ChartViewCommand }>({ seq: 0, command: { type: 'window', width: widthOf(span) } })
  const viewKey = `${symbol}|${tf}|${span}`
  const [viewFor, setViewFor] = useState(viewKey)
  if (viewFor !== viewKey) {
    setViewFor(viewKey)
    setView((v) => ({ seq: v.seq + 1, command: { type: 'reset', width: widthOf(span) } }))
  }
  // The candle under the crosshair (null while the pointer is off the chart).
  const [hover, setHover] = useState<number | null>(null)

  // Non-live feed states (closed, no_feed, reconnecting) keep the candles and overlays on screen.
  const live = true
  const provisional = state.forming ? state.provisional : undefined
  const model = useMemo(
    () => buildMarketModel(state.candles, symbol, active, state.indicators, live),
    [state.candles, symbol, active, state.indicators, live],
  )
  const cells = useMemo(
    () => buildMarketCells(state.candles, symbol, tf, active, state.indicators, live, hover, provisional),
    [state.candles, symbol, tf, active, state.indicators, live, hover, provisional],
  )
  const overlayRev = Object.values(state.indicators).reduce((n, i) => n + i.rev, 0)

  return (
    <>
      <div className="relative min-h-0 flex-1" data-quant-chart-area>
        <QuantChart
          model={model}
          view={view}
          initialWidth={widthOf(span)}
          onHover={setHover}
          overlayRev={overlayRev}
          candleChunks={state.chunks}
        />
        <QuantViewNotice
          status={state.status}
          error={state.error}
          canReconnect={state.canReconnect && viewFailureEffect((state.errorCode ?? 'view_failed') as ViewFailure['code']) === 'reconnect'}
          cancelled={cancelled}
          catalogChanged={state.catalogChanged}
          indicatorRefusal={state.indicatorRefusal}
          detail={state.detail}
          onReconnect={onReconnect}
          onCancel={onCancel}
          empty={state.candles.length === 0}
        />
      </div>
      <QuantReadoutCells cells={cells} endpoint={marketEndpoint(symbol, tf, active)} className="px-5" />
    </>
  )
}
